import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, Envelope, Trash } from "@phosphor-icons/react";

export default function DeleteAccount() {
  return (
    <div className="min-h-screen bg-bone-100" data-testid="delete-account-page">
      <header
        className="bg-bone-100/75 backdrop-blur-xl border-b border-bone-300 sticky top-0 z-10"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-3xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand inline-block"></span> Hush
          </Link>
          <Link to="/" className="text-sm text-ink-500 hover:text-ink-900 flex items-center gap-2" data-testid="delete-back">
            <ArrowLeft size={16} /> Back home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12">
        <p className="eyebrow">Hush · Account & data deletion</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mt-3 mb-8">
          Delete your Hush account
        </h1>
        <p className="text-ink-700 text-lg leading-relaxed mb-12">
          We make leaving as quiet and complete as we make joining. You have two ways
          to permanently delete your Hush account and every piece of data associated
          with it. There is no waiting period and no recovery — when it's gone, it's gone.
        </p>

        {/* Option 1 — in-app */}
        <section className="card p-7 mb-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-full bg-brand text-bone-100 flex items-center justify-center shrink-0">
              <Trash size={20} weight="fill" />
            </div>
            <div>
              <p className="eyebrow mb-1">Recommended · fastest</p>
              <h2 className="font-serif text-2xl">From inside the app</h2>
            </div>
          </div>
          <ol className="space-y-3 list-decimal pl-6 text-ink-700 leading-relaxed">
            <li>Open Hush (iOS, Android, or web)</li>
            <li>Sign in to the account you want to delete</li>
            <li>Tap the <strong>gear icon</strong> in the top-right of your dashboard to open Settings</li>
            <li>Scroll to the <strong>Danger zone</strong> at the bottom</li>
            <li>Tap <strong>"Delete my account"</strong></li>
            <li>Type <code className="bg-bone-200 px-1.5 py-0.5 rounded text-sm">DELETE</code> to confirm</li>
            <li>Tap <strong>"Yes, delete everything"</strong></li>
          </ol>
          <p className="text-sm text-ink-500 mt-5">
            Deletion is immediate. You'll be returned to the landing page and your data is gone.
          </p>
        </section>

        {/* Option 2 — email */}
        <section className="card p-7 mb-12">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-full bg-bone-200 text-ink-700 flex items-center justify-center shrink-0">
              <Envelope size={20} />
            </div>
            <div>
              <p className="eyebrow mb-1">Can't log in? Email us</p>
              <h2 className="font-serif text-2xl">By email request</h2>
            </div>
          </div>
          <p className="text-ink-700 leading-relaxed mb-3">
            If you've lost access to your account (forgotten password, lost device, etc.),
            email us from the address tied to the account:
          </p>
          <a
            href="mailto:help@apphush.online?subject=Account%20deletion%20request"
            className="inline-block btn-primary mb-4"
          >
            help@apphush.online
          </a>
          <p className="text-sm text-ink-500 leading-relaxed">
            Subject: <em>Account deletion request</em>. We'll verify you own the email and
            delete your account within <strong>7 days</strong> (typically same-day).
          </p>
        </section>

        {/* What gets deleted */}
        <section>
          <h2 className="font-serif text-2xl mb-4">What gets deleted</h2>
          <ul className="space-y-2.5 text-ink-700">
            {[
              "Your user profile (email, display name, provider IDs)",
              "Every voice journal entry — transcripts, mood tags, sentiment scores",
              "All login sessions across devices",
              "Push notification subscriptions",
              "Daily reminder preferences and the log of past reminders",
              "Any pending password reset tokens",
            ].map((it, i) => (
              <li key={`del-${i}-${it.slice(0, 24)}`} className="flex items-start gap-2">
                <CheckCircle size={18} weight="fill" className="text-moss shrink-0 mt-0.5" />
                <span>{it}</span>
              </li>
            ))}
          </ul>

          <h3 className="font-serif text-xl mt-8 mb-3">What we keep (and why)</h3>
          <p className="text-ink-700 leading-relaxed text-[15px]">
            We retain only what we're legally required to: anonymous, aggregated billing
            records (sums, dates, no personal data) for the tax-required period in your
            jurisdiction — typically <strong>7 years</strong>. These records contain no
            identifying information about you and cannot be used to reconstruct your account.
          </p>

          <h3 className="font-serif text-xl mt-8 mb-3">Retention period</h3>
          <p className="text-ink-700 leading-relaxed text-[15px]">
            <strong>0 days.</strong> Deletion is immediate when you click the in-app button
            or after we verify your email request. There is no soft-delete or grace period.
          </p>
        </section>

        <div className="mt-12 pt-8 border-t border-bone-300 text-sm text-ink-500">
          Questions? <a href="mailto:help@apphush.online" className="text-brand underline">help@apphush.online</a>
          <span className="mx-2">·</span>
          <Link to="/privacy" className="hover:text-ink-900 underline">Privacy Policy</Link>
        </div>
      </main>
    </div>
  );
}
